import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import type { OrderItem, OrderStatus } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button, Descriptions, Select, Spin, Switch, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { OrderItemMobileCard } from '../../components/OrderItemMobileCard';
import { ORDER_STATUS_OPTIONS, STATUS_CONFIG } from '../../components/OrderStatusTag';
import {
  FormCard,
  FormPageContainer,
  FormPageHeader,
  StyledTable,
} from '../../components/PageLayout';
import { ProductLink } from '../../components/ProductLink';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useListReturn } from '../../hooks/useListReturn';
import {
  useOrder,
  useUpdateOrderCash,
  useUpdateOrderPayment,
  useUpdateOrderStatus,
} from '../../hooks/useOrderQueries';
import { ROUTES } from '../../routes/routes';
import { formatPrice } from '../../utils/currency';

import { OrderHistory } from './OrderHistory';
import { RelatedOrdersByPhone } from './RelatedOrdersByPhone';

const { Text } = Typography;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  min-width: 0;

  @media (max-width: 900px) {
    order: 2;
  }
`;

const SideColumn = styled.div`
  min-width: 0;

  @media (max-width: 900px) {
    order: 1;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px;
`;

const DescriptionsWrapper = styled.div`
  .ant-descriptions-view {
    overflow-x: auto;
  }
`;

const ActionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const ActionLabel = styled(Text)`
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.3px;
`;

const PaymentControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #edf2f7;
`;

const PaymentStatusText = styled.span<{ $active: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? '#22c55e' : '#ff8e8e')};
`;

const PaymentHint = styled.p`
  font-size: 11px;
  color: #64748b;
  margin: 8px 0 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const PAYMENT_LABELS: Record<string, string> = {
  cash_on_delivery: 'Накладений платіж',
  card_transfer: 'На картку',
};

const DELIVERY_LABELS: Record<string, string> = {
  nova_poshta: 'Нова Пошта',
  ukrposhta: 'Укрпошта',
};

export const OrderViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { goToList } = useListReturn(ROUTES.ORDERS);
  const orderId = Number(id);

  const { data: order, isLoading, error } = useOrder(orderId);
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateOrderStatus();
  const { mutate: updatePayment, isPending: isUpdatingPayment } = useUpdateOrderPayment();
  const { mutate: updateCash, isPending: isUpdatingCash } = useUpdateOrderCash();

  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(null);
  const isMobile = useIsMobile();

  const handleBack = () => {
    goToList();
  };

  const handleSaveStatus = () => {
    if (!selectedStatus || !order) return;
    updateStatus(
      { id: order.id, status: selectedStatus },
      { onSuccess: () => setSelectedStatus(null) },
    );
  };

  const handleTogglePaid = (checked: boolean) => {
    if (!order) return;
    updatePayment({ id: order.id, isPaid: checked });
  };

  const handleToggleCash = (checked: boolean) => {
    if (!order) return;
    updateCash({ id: order.id, isCashed: checked });
  };

  if (isLoading) {
    return (
      <FormPageContainer>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </FormPageContainer>
    );
  }

  if (error || !order) {
    return (
      <FormPageContainer>
        <FormPageHeader>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <h2>Замовлення</h2>
        </FormPageHeader>
        <Text type="danger">{error?.message || 'Замовлення не знайдено'}</Text>
      </FormPageContainer>
    );
  }

  const currentStatus = selectedStatus ?? order.status;
  const orderNumber = String(order.id).padStart(4, '0');

  const formattedDate = new Date(order.createdAt).toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const itemColumns: TableProps<OrderItem>['columns'] = [
    {
      title: 'Товар',
      key: 'productName',
      render: (_: unknown, record: OrderItem) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.image ? (
            <img
              src={record.image}
              alt={record.productName}
              style={{ width: 50, height: 50, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: 6,
                background: '#f1f5f9',
                flexShrink: 0,
              }}
            />
          )}
          <ProductLink slug={record.productId}>{record.productName}</ProductLink>
        </div>
      ),
    },
    {
      title: 'Ціна',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => formatPrice(price),
    },
    {
      title: 'Кількість',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Сума',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => formatPrice(total),
    },
  ];

  return (
    <FormPageContainer>
      <FormPageHeader>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
        <h2>Замовлення №{orderNumber}</h2>
      </FormPageHeader>

      <TwoColumnGrid>
        <MainColumn>
          <FormCard>
            <SectionTitle>Інформація про замовлення</SectionTitle>
            <DescriptionsWrapper>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Дата">{formattedDate}</Descriptions.Item>
                <Descriptions.Item label="Отримувач">
                  {order.recipient.lastName} {order.recipient.firstName}
                </Descriptions.Item>
                <Descriptions.Item label="Телефон">{order.recipient.phone}</Descriptions.Item>
                {order.recipient.email && (
                  <Descriptions.Item label="Email">{order.recipient.email}</Descriptions.Item>
                )}
                <Descriptions.Item label="Спосіб оплати">
                  {PAYMENT_LABELS[order.paymentMethod]}
                </Descriptions.Item>
              </Descriptions>
            </DescriptionsWrapper>
          </FormCard>

          <FormCard>
            <SectionTitle>Доставка</SectionTitle>
            <DescriptionsWrapper>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Служба доставки">
                  {DELIVERY_LABELS[order.delivery.provider]}
                </Descriptions.Item>
                <Descriptions.Item label="Населений пункт">
                  {order.delivery.settlement}
                </Descriptions.Item>
                {order.delivery.area && (
                  <Descriptions.Item label="Область">{order.delivery.area}</Descriptions.Item>
                )}
                {order.delivery.warehouse && (
                  <Descriptions.Item label="Відділення">
                    {order.delivery.warehouse}
                  </Descriptions.Item>
                )}
                {order.delivery.address && (
                  <Descriptions.Item label="Адреса">{order.delivery.address}</Descriptions.Item>
                )}
                {order.delivery.postalCode && (
                  <Descriptions.Item label="Поштовий індекс">
                    {order.delivery.postalCode}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </DescriptionsWrapper>
          </FormCard>

          {order.comment && (
            <FormCard>
              <SectionTitle>Коментар</SectionTitle>
              <Text>{order.comment}</Text>
            </FormCard>
          )}

          <FormCard>
            <SectionTitle>Товари</SectionTitle>
            {isMobile ? (
              <>
                {order.items.map((item) => (
                  <OrderItemMobileCard key={item.productId} item={item} />
                ))}
                <div
                  style={{
                    borderTop: '1px solid #edf2f7',
                    paddingTop: 10,
                    marginTop: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text strong>Загалом</Text>
                  <Text strong>{formatPrice(order.total)}</Text>
                </div>
              </>
            ) : (
              <StyledTable
                columns={itemColumns}
                dataSource={order.items}
                rowKey="productId"
                pagination={false}
                scroll={{ x: 650 }}
                summary={() => (
                  <StyledTable.Summary.Row>
                    <StyledTable.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Загалом</Text>
                    </StyledTable.Summary.Cell>
                    <StyledTable.Summary.Cell index={1}>
                      <Text strong>{formatPrice(order.total)}</Text>
                    </StyledTable.Summary.Cell>
                  </StyledTable.Summary.Row>
                )}
              />
            )}
          </FormCard>

          <FormCard>
            <SectionTitle>Історія змін</SectionTitle>
            <OrderHistory orderId={order.id} />
          </FormCard>

          <FormCard>
            <SectionTitle>Інші замовлення з цим номером</SectionTitle>
            <RelatedOrdersByPhone orders={order.relatedOrders ?? []} />
          </FormCard>
        </MainColumn>

        <SideColumn>
          <FormCard>
            <SectionTitle>Управління</SectionTitle>

            <ActionBlock>
              <ActionLabel>Статус замовлення</ActionLabel>
              <Select
                style={{ width: '100%' }}
                value={currentStatus}
                onChange={(value) => setSelectedStatus(value as OrderStatus)}
                options={ORDER_STATUS_OPTIONS}
                labelRender={({ value }) => {
                  const config = STATUS_CONFIG[value as OrderStatus];
                  return config ? <Tag color={config.color}>{config.label}</Tag> : String(value);
                }}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveStatus}
                loading={isUpdatingStatus}
                disabled={!selectedStatus || selectedStatus === order.status}
                block
              >
                Зберегти статус
              </Button>
            </ActionBlock>

            <ActionBlock>
              <ActionLabel>Статус оплати</ActionLabel>
              <PaymentControl>
                <PaymentStatusText $active={order.isPaid}>
                  {order.isPaid ? 'Оплачено' : 'Очікує оплати'}
                </PaymentStatusText>
                <Switch
                  checked={order.isPaid}
                  onChange={handleTogglePaid}
                  loading={isUpdatingPayment}
                />
              </PaymentControl>
              <PaymentHint>Перемкніть, щоб підтвердити отримання коштів від клієнта.</PaymentHint>
            </ActionBlock>

            {order.delivery.provider === 'nova_poshta' && (
              <ActionBlock>
                <ActionLabel>Каса (Нова Пошта)</ActionLabel>
                <PaymentControl>
                  <PaymentStatusText $active={order.isCashed}>
                    {order.isCashed ? 'Проведено' : 'Не проведено'}
                  </PaymentStatusText>
                  <Switch
                    checked={order.isCashed}
                    onChange={handleToggleCash}
                    loading={isUpdatingCash}
                  />
                </PaymentControl>
                <PaymentHint>Перемкніть після проведення через касу Нової Пошти.</PaymentHint>
              </ActionBlock>
            )}
          </FormCard>
        </SideColumn>
      </TwoColumnGrid>
    </FormPageContainer>
  );
};
